import { useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';
import { EmployeeType } from '@prisma/client';
import { Breadcrumb, Button, Dropdown, Input, Layout, Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { Role } from '@app/domain-interfaces';
import { Auth, PageLayout, Tables } from '@app/web-components';

import { CreatedByColumn, CreatedOnColumn, UpdatedByColumn, UpdatedOnColumn } from '../../../../../common';
import { useFindManyAgsRoleToProcessMap, useRoleProcessMapActions } from '../libs';
import { LandingPageItem } from '../libs/landing-page-item';

export const LandingPage = () => {
  const { getDeleteModalProps, getDetailsModalProps, getDropdownButtonProps } = useRoleProcessMapActions();
  const { user } = Auth.useUserAuth();

  const columns = useMemo(() => {
    const _columns: ColumnsType<LandingPageItem> = [
      {
        title: 'AGS Role',
        dataIndex: ['access', 'name'],
        sorter: true,
      },
      {
        title: 'Process',
        dataIndex: ['process', 'name'],
        sorter: true,
      },
      {
        title: 'Employee Type',
        dataIndex: 'employeeType',
        sorter: true,
        filters: [
          { text: 'Intel', value: EmployeeType.INTEL },
          { text: 'Contingent Worker', value: EmployeeType.CONTINGENT_WORKER },
        ],
      },
      UpdatedByColumn,
      UpdatedOnColumn,
      CreatedByColumn,
      { ...CreatedOnColumn, defaultSortOrder: 'descend' },
    ];

    if (user?.roles[Role.RULE_ADMIN]) {
      _columns.unshift({
        title: 'Action',
        width: 120,
        render: (_, record) => <Dropdown.Button {...getDropdownButtonProps(record)} />,
      });
    }

    return _columns;
  }, [getDropdownButtonProps, user?.roles]);

  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<LandingPageItem>('Role Process Map', columns, {
    globalSearchFields: ['access.name', 'process.name'],
  });

  const queryResult = useFindManyAgsRoleToProcessMap<LandingPageItem>(
    getQuery({
      include: { access: true, process: true, createdByUser: true, updatedByUser: true },
    }),
  );

  return (
    <>
      <Modal {...getDeleteModalProps()} />
      <Modal {...getDetailsModalProps()} />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: 'Admin' }, { title: 'Role Process Map' }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Role Process Map" />
        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} placeholder="Search by AGS Role Name or DMS Process Name" />,
              user?.roles[Role.RULE_ADMIN] ? <Button {...getCreateButtonProps()} /> : undefined,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink {...getCSVLinkProps(queryResult.data?.data)} />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
